import React from 'react';
import Icon from '../../../components/AppIcon';

const TransformationTimeline = () => {
  const timelineSteps = [
    {
      phase: "Assessment",
      duration: "2-4 weeks",
      title: "AI Readiness Evaluation",
      description: "Comprehensive analysis of current AI infrastructure, capabilities, and strategic objectives.",
      icon: "Search",
      color: "bg-blue-500",
      activities: [
        "Current state assessment",
        "Vendor dependency analysis",
        "Technical capability audit",
        "Strategic alignment review"
      ]
    },
    {
      phase: "Planning",
      duration: "3-6 weeks",
      title: "Blueprint Development",
      description: "Custom AI Core Blueprint creation with detailed implementation roadmap and resource requirements.",
      icon: "FileText",
      color: "bg-purple-500",
      activities: [
        "Architecture design",
        "Technology stack selection",
        "Migration strategy planning",
        "Risk mitigation framework"
      ]
    },
    {
      phase: "Foundation",
      duration: "6-12 weeks",
      title: "Infrastructure Setup",
      description: "Open-source AI platform deployment with security, scalability, and governance frameworks.",
      icon: "Server",
      color: "bg-green-500",
      activities: [
        "Platform deployment",
        "Security implementation",
        "Monitoring setup",
        "Initial team training"
      ]
    },
    {
      phase: "Migration",
      duration: "8-16 weeks",
      title: "Workload Transition",
      description: "Systematic migration of AI workloads from proprietary platforms to open-source solutions.",
      icon: "ArrowRightLeft",
      color: "bg-orange-500",
      activities: [
        "Data migration",
        "Model porting",
        "Integration testing",
        "Performance optimization"
      ]
    },
    {
      phase: "Optimization",
      duration: "4-8 weeks",
      title: "Performance Tuning",
      description: "Fine-tuning of AI models and infrastructure for optimal performance and cost efficiency.",
      icon: "Zap",
      color: "bg-yellow-500",
      activities: [
        "Model optimization",
        "Resource scaling",
        "Cost optimization",
        "Performance monitoring"
      ]
    },
    {
      phase: "Independence",
      duration: "Ongoing",
      title: "Full AI Autonomy",
      description: "Complete organizational AI independence with internal capabilities and continuous improvement.",
      icon: "Trophy",
      color: "bg-brand-primary",
      activities: [
        "Internal capability building",
        "Continuous improvement",
        "Innovation acceleration",
        "Strategic advantage realization"
      ]
    }
  ];

  return (
    <div className="bg-white rounded-xl shadow-executive border border-gray-200 p-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-text-primary mb-4">
          Transformation Timeline
        </h2>
        <p className="text-lg text-text-secondary max-w-3xl mx-auto">
          Our proven methodology takes organizations from vendor dependency to AI independence 
          through a structured, risk-mitigated approach with clear milestones and measurable outcomes.
        </p>
      </div>
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200 hidden lg:block" />

        <div className="space-y-12">
          {timelineSteps?.map((step, index) => (
            <div key={index} className="relative flex flex-col lg:flex-row lg:items-start">
              {/* Timeline Dot */}
              <div className="hidden lg:flex absolute left-6 w-4 h-4 rounded-full bg-white border-4 border-gray-200 z-10" />
              
              {/* Phase Icon and Info */}
              <div className="lg:ml-20 flex-1">
                <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                  {/* Icon and Phase Info */}
                  <div className="flex items-center space-x-4 sm:flex-col sm:items-center sm:space-x-0 sm:space-y-2 sm:w-32 flex-shrink-0">
                    <div className={`w-16 h-16 ${step?.color} rounded-xl flex items-center justify-center`}>
                      <Icon name={step?.icon} size={24} className="text-white" />
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium text-text-secondary uppercase tracking-wide">
                        {step?.phase}
                      </div>
                      <div className="text-sm text-brand-primary font-semibold">
                        {step?.duration}
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-text-primary mb-3">
                      {step?.title}
                    </h3>
                    <p className="text-text-secondary mb-4">
                      {step?.description}
                    </p>

                    {/* Activities */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {step?.activities?.map((activity, activityIndex) => (
                        <div key={activityIndex} className="flex items-center space-x-2">
                          <Icon name="Check" size={16} className="text-green-500 flex-shrink-0" />
                          <span className="text-sm text-text-secondary">{activity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Summary Stats */}
      <div className="mt-12 pt-8 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="text-3xl font-bold text-brand-primary mb-2">6-18 months</div>
            <div className="text-text-secondary">Average Timeline</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-brand-primary mb-2">40-70%</div>
            <div className="text-text-secondary">Cost Reduction</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-brand-primary mb-2">100%</div>
            <div className="text-text-secondary">AI Independence</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransformationTimeline;